import React from 'react';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { Gap8 } from 'src/styles/styles';
import {
  findLastShipmentForInk,
  findLastShipmentForPaper
} from './helpers/findLastShipment';
import { SubscriptionsProps } from 'src/components/SubscriptionsCard/types';
import { FulfillmentStatus } from 'src/utils/enums';
import { TranslateType } from 'src/contexts/dependencyManager/types';
import { hasLastShipment } from './helpers/hasLastShipment';
import { formatShippingDate } from 'src/utils/masks';

interface ShipmentStatusProps {
  content: {
    status: FulfillmentStatus;
    shipmentDate: string;
  };
  type: 'ink' | 'paper';
  translate: TranslateType;
}

const renderShipmentStatus = ({
  content,
  type,
  translate
}: ShipmentStatusProps) => {
  const date = formatShippingDate(content.shipmentDate);
  if (content.status === FulfillmentStatus.SHIPPED) {
    return type === 'ink'
      ? translate('subscriptions.inkShippedOn', {
          defaultValue: 'Ink shipped on {{date}}',
          replace: { date }
        })
      : translate('subscriptions.paperShippedOn', {
          defaultValue: 'Paper shipped on {{date}}',
          replace: { date }
        });
  } else {
    return type === 'ink'
      ? translate('subscriptions.inkDeliveredOn', {
          defaultValue: 'Ink delivered on {{date}}',
          replace: { date }
        })
      : translate('subscriptions.paperDeliveredOn', {
          defaultValue: 'Paper delivered on {{date}}',
          replace: { date }
        });
  }
};

const ShipmentHistoryContent = ({
  subscription
}: {
  subscription: SubscriptionsProps;
}) => {
  const { translate } = useDependencyManagerContext();

  const lastShipmentInk = findLastShipmentForInk(subscription);
  const lastShipmentPaper = findLastShipmentForPaper(subscription);
  const hasLastShipmentInk = hasLastShipment(lastShipmentInk);
  const hasLastShipmentPaper = hasLastShipment(lastShipmentPaper);

  if (!hasLastShipmentInk && !hasLastShipmentPaper) {
    return null;
  }

  return (
    <Gap8>
      {hasLastShipmentInk && (
        <span
          className="caption"
          role="status"
          aria-label="Shipment status for ink"
        >
          {renderShipmentStatus({
            content: lastShipmentInk,
            type: 'ink',
            translate
          })}
        </span>
      )}
      {hasLastShipmentPaper && (
        <span
          className="caption"
          role="status"
          aria-label="Shipment status for paper"
        >
          {renderShipmentStatus({
            content: lastShipmentPaper,
            type: 'paper',
            translate
          })}
        </span>
      )}
    </Gap8>
  );
};

export default ShipmentHistoryContent;
